import React, { useMemo } from 'react';

import { CheckOutlined } from '@ant-design/icons/lib';
import { Form, Input } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import DropdownWithCheckboxSelection from 'common/SelectAdvancedWithGrouping/DropdownWithCheckboxSelection';
import { SelectAdvancedWithGroupingContext } from 'common/SelectAdvancedWithGrouping/SelectAdvancedWithGroupingContext';
import { DataType } from 'store/slices/globalSlice';
import type { ApartmentType } from 'types/accommodations';

import { useCMASummaryRoomTypeFilterContext } from './CMASummaryRoomTypeFilterContext';
import type { RoomTypeGroupOption } from '../types';

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--line-gray);
  max-height: 200px;
  overflow-y: auto;
  padding: 10px 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const ItemsTitle = styled.span`
  font-family: var(--font-bold);
`;

const SectionActions = styled.div`
  display: flex;
  gap: 10px;

  span {
    color: var(--dark-blue);
    font-size: 12px;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }

    &.disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  cursor: pointer;
  gap: 10px;
`;

const ItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const EmptyState = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const CMASummaryRoomTypeFilterSelection = () => {
  const { form, options, groupedOptions } = useCMASummaryRoomTypeFilterContext();

  const getGroupVariantIds = (group: RoomTypeGroupOption) => group.apartmentTypes.map((type) => type.id);

  const convertToDataType = (variants: ApartmentType[]): DataType[] =>
    variants.map((variant) => ({
      id: variant.id,
      type: variant.type,
      created_at: variant.created_at ?? '',
      updated_at: variant.updated_at ?? '',
    }));

  const getOrderedValues = (values: number[]) => {
    const valueSet = new Set(values);
    return options.filter((option) => valueSet.has(option.value)).map((option) => option.value);
  };

  const allVariantIds = useMemo(() => groupedOptions.flatMap((group) => getGroupVariantIds(group)), [groupedOptions]);

  return (
    <>
      <Form.Item name="roomTypes" hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item noStyle shouldUpdate={(prev, next) => prev?.roomTypes !== next?.roomTypes}>
        {({ getFieldValue }) => {
          const selectedValues: number[] = getFieldValue('roomTypes') || [];
          const hasSelectedVariants = (group: RoomTypeGroupOption) =>
            group.apartmentTypes.some((apartmentType) => selectedValues.includes(apartmentType.id));
          const selectedItems = groupedOptions.filter((group) => hasSelectedVariants(group));
          const nonSelectedItems = groupedOptions.filter((group) => !hasSelectedVariants(group));

          const allSelected =
            allVariantIds.length > 0 && allVariantIds.every((variantId) => selectedValues.includes(variantId));
          const noneSelected = selectedValues.length === 0;

          const handleSelectGroup = (group: RoomTypeGroupOption) => {
            const orderedValues = getOrderedValues([...selectedValues, ...getGroupVariantIds(group)]);
            form.setFieldsValue({ roomTypes: orderedValues });
          };

          const handleDeselectGroup = (group: RoomTypeGroupOption) => {
            const groupVariantIds = getGroupVariantIds(group);
            form.setFieldsValue({
              roomTypes: selectedValues.filter((value: number) => !groupVariantIds.includes(value)),
            });
          };

          const handleSelectAll = () => {
            form.setFieldsValue({ roomTypes: getOrderedValues(allVariantIds) });
          };

          const handleSelectNone = () => {
            form.setFieldsValue({ roomTypes: [] });
          };

          const addVariants = (ids: number[]) => {
            const orderedValues = getOrderedValues([...selectedValues, ...ids]);
            form.setFieldsValue({ roomTypes: orderedValues });
          };

          const removeVariants = (ids: number[]) => {
            form.setFieldsValue({
              roomTypes: selectedValues.filter((value: number) => !ids.includes(value)),
            });
          };

          const renderItem = (group: RoomTypeGroupOption, isSelected: boolean) => (
            <Item key={group.id} onClick={() => (isSelected ? handleDeselectGroup(group) : handleSelectGroup(group))}>
              {isSelected && <CheckOutlined style={{ color: 'var(--positive-green)', fontSize: 18 }} />}
              <ItemContent>
                <div>{group.label && <span>{group.label}</span>}</div>
                {isSelected && (
                  <DropdownWithCheckboxSelection apartmentTypes={convertToDataType(group.apartmentTypes)} />
                )}
              </ItemContent>
            </Item>
          );

          const renderBulkActions = () => (
            <SectionActions>
              <span onClick={handleSelectAll} className={classNames({ disabled: allSelected })}>
                Select All
              </span>
              <span onClick={handleSelectNone} className={classNames({ disabled: noneSelected })}>
                Select None
              </span>
            </SectionActions>
          );

          return (
            <SelectAdvancedWithGroupingContext.Provider
              value={{
                selectedGroupOptions: selectedValues,
                addItemsToSelectedGroupOptions: addVariants,
                removeItemsFromSelectedGroupOptions: removeVariants,
                filteredSelectedItems: [],
                filteredNonSelectedItems: [],
                handleSelectItem: () => undefined,
                handleDeselectItem: () => undefined,
                handleSelectAll: () => undefined,
                handleSelectNone: () => undefined,
                handleSearch: () => undefined,
                handleApplySelection: () => undefined,
              }}
            >
              <ItemsContainer className="mini-scroll">
                <SectionHeader>
                  <ItemsTitle>SELECTED</ItemsTitle>
                  {selectedItems.length > 0 ? renderBulkActions() : null}
                </SectionHeader>
                {selectedItems.length > 0 ? (
                  <ItemsList>{selectedItems.map((group) => renderItem(group, true))}</ItemsList>
                ) : (
                  <EmptyState>No room types selected.</EmptyState>
                )}
              </ItemsContainer>
              <ItemsContainer className="mini-scroll">
                <SectionHeader>
                  <ItemsTitle>NOT SELECTED</ItemsTitle>
                  {selectedItems.length === 0 ? renderBulkActions() : null}
                </SectionHeader>
                {nonSelectedItems.length > 0 ? (
                  <ItemsList>{nonSelectedItems.map((group) => renderItem(group, false))}</ItemsList>
                ) : (
                  <EmptyState>All room types selected.</EmptyState>
                )}
              </ItemsContainer>
            </SelectAdvancedWithGroupingContext.Provider>
          );
        }}
      </Form.Item>
    </>
  );
};

export default CMASummaryRoomTypeFilterSelection;
